

import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Select, Button, Table, DatePicker, Row, Col, Space, Typography, message, Modal, Spin } from 'antd';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { useParams } from 'react-router-dom';

const { Option } = Select;
const { Title } = Typography;

const PurchaseOrderEdit = () => {
    const [form] = Form.useForm();
    const [suppliers, setSuppliers] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [products, setProducts] = useState([{ product: null, quantity: '', buying_price: '', selling_price: '' }]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false); // Loader for data fetching
    const [orderNumber, setOrderNumber] = useState('');
    const { id } = useParams();

    // Fetch suppliers and products once on component mount
    useEffect(() => {
        const fetchSuppliersAndProducts = async () => {
            try {
                setFetching(true);
                const [suppliersResponse, productsResponse] = await Promise.all([
                    axiosInstance.get('/vendors'),
                    axiosInstance.get('/products'),
                ]);
                setSuppliers(suppliersResponse.data.vendors);
                setProductsList(productsResponse.data.products);
            } catch (error) {
                message.error("Failed to fetch suppliers or products.");
            } finally {
                setFetching(false);
            }
        };

        fetchSuppliersAndProducts();
    }, []);

    // Fetch order details after products are available
    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                setFetching(true);
                const orderResponse = await axiosInstance.get(`/purchase-orders/${id}`);
                const { supplier, order_date, order_type, order_number, items } = orderResponse.data;

                setOrderNumber(order_number);
                form.setFieldsValue({
                    order_number,
                    supplier: supplier ? supplier.id : '',
                    order_date: order_date ? moment(order_date) : null,
                    order_type,
                });

                const initialProducts = items.map((prod) => ({
                    product: productsList.find((p) => p.id === prod.product_id) || null,
                    quantity: prod.quantity,
                    buying_price: prod.cost,
                    selling_price: prod.selling_price,
                }));
                setProducts(initialProducts);
            } catch (error) {
                message.error("Failed to fetch purchase order details.");
            } finally {
                setFetching(false);
            }
        };

        if (productsList.length) {
            fetchOrderDetails();
        }
    }, [id, productsList]);

    const handleProductChange = (index, field, value) => {
        const updatedProducts = [...products];
        updatedProducts[index][field] = value;
        setProducts(updatedProducts);
    };

    const addProductRow = () => {
        setProducts([...products, { product: null, quantity: '', buying_price: '', selling_price: '' }]);
    };

    const removeProductRow = (index) => {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);
    };

    const confirmSubmit = (values) => {
        Modal.confirm({
            title: 'Confirm Order Update',
            content: 'Are you sure you want to update this purchase order?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => handleSubmit(values),
        });
    };

    const validateProducts = () => {
        return products.every(product => product.product && product.quantity > 0 && product.buying_price > 0);
    };

    const handleSubmit = async (values) => {
        if (!validateProducts()) {
            message.error("Ensure all products have valid values before submitting.");
            return;
        }

        setLoading(true);

        const payload = {
            supplier_id: values.supplier,
            order_date: values.order_date.format('YYYY-MM-DD'),
            order_type: values.order_type,
            products: products.map((product) => ({
                id: product.product.id,
                quantity: product.quantity,
                buying_price: product.buying_price,
                selling_price: product.selling_price,
            })),
        };

        try {
            await axiosInstance.put(`/purchase-orders/${id}`, payload);
            message.success("Purchase Order Updated Successfully!");
        } catch (error) {
            message.error(error.response?.data?.message || "Error updating Purchase Order.");
        } finally {
            setLoading(false);
        }
    };

    const productColumns = [
        {
            title: 'Product',
            dataIndex: 'product',
            render: (_, record, index) => (
                <Select
                    showSearch
                    value={record.product ? record.product.id : ''}
                    onChange={(value) => handleProductChange(index, 'product', productsList.find(prod => prod.id === value))}
                    style={{ width: '200px' }}
                    placeholder="Select Product"
                    optionFilterProp="children" // Enables search based on the Option's text
                    filterOption={(input, option) =>
                        option?.children.toLowerCase().includes(input.toLowerCase())
                    } // Custom filter logic for better search
                >
                    {productsList.map((prod) => (
                        <Option key={prod.id} value={prod.id}>
                            {prod.name}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.quantity}
                    onChange={(e) => handleProductChange(index, 'quantity', parseInt(e.target.value, 10) || 1)}
                    min={1}
                    placeholder="Enter Quantity"
                />
            ),
        },
        {
            title: 'Buying Price',
            dataIndex: 'buying_price',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.buying_price}
                    onChange={(e) => handleProductChange(index, 'buying_price', parseFloat(e.target.value) || 0)}
                    min={0}
                    placeholder="Enter Price"
                />
            ),
        },
        {
            title: 'Selling Price',
            dataIndex: 'selling_price',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.selling_price}
                    onChange={(e) => handleProductChange(index, 'selling_price', parseFloat(e.target.value) || 0)}
                    min={0}
                    placeholder="Enter Price"
                />
            ),
        },
        {
            title: 'Total',
            render: (_, record) => (
                (record.quantity * record.buying_price).toFixed(2)
            ),
        },
        {
            title: 'Action',
            render: (_, __, index) => (
                index > 0 && (
                    <Button type="link" danger onClick={() => removeProductRow(index)}>
                        Remove
                    </Button>
                )
            ),
        },
    ];

    const { totalQuantity, totalPrice } = useMemo(() => {
        let totalQty = 0;
        let totalPrc = 0;

        products.forEach(({ quantity, buying_price }) => {
            totalQty += quantity || 0;
            totalPrc += (quantity || 0) * (buying_price || 0);
        });

        return { totalQuantity: totalQty, totalPrice: totalPrc.toFixed(2) };
    }, [products]);

    return (
        <Spin spinning={fetching} tip="Loading...">
            <div>
                <Title level={3}>Edit Purchase Order</Title>
                <Form form={form} layout="vertical" onFinish={confirmSubmit}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label="Order Number" name="order_number">
                                <Input value={orderNumber} disabled />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                label="Supplier"
                                name="supplier"
                                // rules={[{ required: true, message: 'Please select a supplier' }]}
                                >
                                <Select placeholder="Select Supplier">
                                    {suppliers.map((supplier) => (
                                        <Option key={supplier.id} value={supplier.id}>
                                            {supplier.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                label="Order Date"
                                name="order_date"
                                rules={[{ required: true, message: 'Please select an order date' }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                label="Order Type"
                                name="order_type"
                                rules={[{ required: true, message: 'Please select an order type' }]}>
                                <Select>
                                    <Option value="local">Local</Option>
                                    <Option value="import">Import</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Table
                        dataSource={products}
                        columns={productColumns}
                        pagination={false}
                        rowKey={(record, index) => index}
                        style={{ marginBottom: '20px' }}
                        scroll={{ x: 'max-content' }}
                    />

                    <Space style={{ marginBottom: '20px', float: 'right' }}>
                        <Button type="dashed" onClick={addProductRow}>
                            Add Product
                        </Button>
                    </Space>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <strong>Total Quantity:</strong> {totalQuantity}
                        </Col>
                        <Col xs={24} sm={12} md={6} style={{ textAlign: 'right' }}>
                            <strong>Total Price:</strong> {totalPrice}
                        </Col>
                    </Row>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Update Order
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </Spin>
    );
};

export default PurchaseOrderEdit;

